import { Checkbox, Form, Input } from "antd";
import React, { useEffect, useMemo, useRef } from "react";
import {
  cardioDiagnosisOptions,
  orthoDiagnosisOptions,
} from "../../../../IpEMR/Options/physioOptions";
import {
  neuroDiagnosisOptions,
  spinalOptions,
  sportsDiagnosisOptions,
  strokeHeadInjuryOptions,
} from "../OpPhysioOptions";
import DeselectableRadioGroup from "../../../../../blocks/FormItems/DeselectableRadioGroup";
import DynamicInputFields from "../../../../../blocks/FormItems/DynamicInputFields";

const OpFunctionalDiagnosis = ({ onValuesChange, formData, formType }) => {
  const [form] = Form.useForm();
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const watchedDiagnosis = Form.useWatch("diagnosis", form);
  const showOthers =
    Array.isArray(watchedDiagnosis) && watchedDiagnosis.includes("others");

  const functionalDiagnosisHydrateKey = useMemo(() => {
    const fd = formData?.functional_diagnosis;
    return JSON.stringify({
      id: formData?.id,
      diagnosis: fd?.diagnosis,
      stroke: fd?.stroke,
      head_injury: fd?.head_injury,
      spinal_cord_injury: fd?.spinal_cord_injury,
    });
  }, [
    formData?.id,
    formData?.functional_diagnosis?.diagnosis,
    formData?.functional_diagnosis?.stroke,
    formData?.functional_diagnosis?.head_injury,
    formData?.functional_diagnosis?.spinal_cord_injury,
  ]);

  // Re-hydrate when record id or structured selections change (not on every comment keystroke).
  useEffect(() => {
    const fd = formDataRef.current?.functional_diagnosis;
    if (!fd || typeof fd !== "object" || Object.keys(fd).length === 0) return;
    form.setFieldsValue(fd);
    const hasOthers =
      Array.isArray(fd.diagnosis) && fd.diagnosis.includes("others");
    if (!hasOthers) {
      form.setFieldValue("diagnosis_others", []);
    }
  }, [form, functionalDiagnosisHydrateKey]);

  const getOptions = () => {
    switch (formType) {
      case "cardio":
        return cardioDiagnosisOptions;
      case "neuro":
        return neuroDiagnosisOptions;
      case "ortho":
        return orthoDiagnosisOptions;
      case "sports":
        return sportsDiagnosisOptions;
      default:
        return [];
    }
  };

  const handleChange = (values) => {
    const normalized = (values || []).map((v) =>
      typeof v === "string" ? v : v?.value ?? v
    );
    const nextShowOthers = normalized.includes("others");
    if (!nextShowOthers) {
      form.setFieldValue("diagnosis_others", []);
    } else {
      const existing = form.getFieldValue("diagnosis_others");
      if (!Array.isArray(existing) || existing.length === 0) {
        form.setFieldValue("diagnosis_others", [{ remarks: "" }]);
      }
    }
   
    form.setFieldValue("diagnosis", normalized);
    onValuesChange("functional_diagnosis", form.getFieldsValue(true));
  };

  // const showOthers = form.getFieldValue("diagnosis")
  return (
    <>
      <Form
        key={formData?.id != null ? `op-fd-${formData.id}` : "op-fd-new"}
        form={form}
        id="op-functional-diagnosis"
        onValuesChange={(cur, all) =>
          onValuesChange("functional_diagnosis", all)
        }
        layout="vertical"
        className="ip-forms"
        initialValues={formData?.functional_diagnosis}
      >
        <Form.Item label="Functional Diagnosis" name={"diagnosis"}>
          <Checkbox.Group
            onChange={handleChange}
            options={getOptions()}
            style={{ display: "flex", flexWrap: "wrap", gap: 30 }}
          />
        </Form.Item>

        {showOthers && (
          <DynamicInputFields
            field_key={"diagnosis_others"}
            form={form}
            inputStyle={{ maxWidth: 800 }}
          />
        )}

        {formType === "neuro" && (
          <>
            <DeselectableRadioGroup
              label={"Stroke"}
              name={"stroke"}
              options={strokeHeadInjuryOptions}
              form={form}
            />
            <DeselectableRadioGroup
              label={"Head Injury"}
              name={"head_injury"}
              options={strokeHeadInjuryOptions}
              form={form}
            />
            <DeselectableRadioGroup
              label={"Spinal Cord Injury"}
              name={"spinal_cord_injury"}
              options={spinalOptions}
              form={form}
            />
          </>
        )}
        <Form.Item name={"diagnosis_comments"}>
          <Input.TextArea rows={3} placeholder="Comments" />
        </Form.Item>
      </Form>
    </>
  );
};

export default OpFunctionalDiagnosis;
